import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { 
  Calendar as CalendarIcon, Plus, RefreshCw, Loader2, 
  Clock, MapPin, ChevronLeft, ChevronRight, ExternalLink,
  AlertCircle, Trash2, ArrowDownUp, CheckCircle
} from 'lucide-react';
import * as api from '../services/api';

const CalendarPanel = ({ currentUser, tareas = [], onSyncTask, onTasksUpdated }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  
  // Create event modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEvent, setNewEvent] = useState({
    summary: '',
    description: '',
    location: '',
    start_date: '',
    start_time: '09:00',
    end_date: '',
    end_time: '10:00'
  });

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get events for current month
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
      
      const response = await api.listCalendarEvents(
        currentUser.id,
        'primary',
        startOfMonth.toISOString(),
        endOfMonth.toISOString()
      );
      setEvents(response.data || []);
    } catch (err) {
      console.error('Error loading events:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('not connected')) {
        setError('Calendar no está conectado. Ve a Admin > Google para conectar tu cuenta.');
      } else {
        setError('Error cargando eventos: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser.id, currentDate]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleCreateEvent = async () => {
    if (!newEvent.summary || !newEvent.start_date || !newEvent.end_date) {
      alert('Por favor completa los campos requeridos');
      return;
    }
    
    try {
      setCreating(true);
      
      const startDateTime = new Date(`${newEvent.start_date}T${newEvent.start_time}:00`);
      const endDateTime = new Date(`${newEvent.end_date}T${newEvent.end_time}:00`);
      
      await api.createCalendarEvent(currentUser.id, {
        summary: newEvent.summary,
        description: newEvent.description,
        location: newEvent.location,
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString()
      });
      
      setShowCreateModal(false);
      setNewEvent({
        summary: '',
        description: '',
        location: '',
        start_date: '',
        start_time: '09:00',
        end_date: '',
        end_time: '10:00'
      });
      loadEvents();
    } catch (err) {
      console.error('Error creating event:', err);
      alert('Error creando evento: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    
    try {
      await api.deleteCalendarEvent(currentUser.id, eventId);
      loadEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Error eliminando evento');
    }
  };

  const syncTaskToCalendar = async (tarea) => {
    if (!tarea.due_date) {
      alert('La tarea no tiene fecha de vencimiento');
      return;
    }
    
    try {
      await api.syncTaskToCalendar(tarea.id);
      alert('Tarea sincronizada con Google Calendar');
      loadEvents();
      if (onSyncTask) onSyncTask(tarea.id);
    } catch (err) {
      console.error('Error syncing task:', err);
      alert('Error sincronizando tarea: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSyncFromCalendar = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      const response = await api.syncTasksFromCalendar(currentUser.id);
      setSyncMessage({
        type: 'success',
        text: response.data.message || 'Sincronización completada'
      });
      loadEvents();
      if (onTasksUpdated) onTasksUpdated();
    } catch (err) {
      console.error('Error syncing from calendar:', err);
      setSyncMessage({
        type: 'error',
        text: 'Error sincronizando: ' + (err.response?.data?.detail || err.message)
      });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const handleSyncAllToCalendar = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      const response = await api.syncAllTasksToCalendar(currentUser.id);
      setSyncMessage({
        type: 'success',
        text: response.data.message || 'Sincronización completada'
      });
      loadEvents();
    } catch (err) {
      console.error('Error syncing to calendar:', err);
      setSyncMessage({
        type: 'error',
        text: 'Error sincronizando: ' + (err.response?.data?.detail || err.message)
      });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const formatEventTime = (event) => {
    if (!event.start) return '';
    
    const startDate = event.start.dateTime || event.start.date;
    if (!startDate) return '';
    
    const date = new Date(startDate);
    return date.toLocaleTimeString('es-PR', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatEventDate = (event) => {
    if (!event.start) return '';
    
    const startDate = event.start.dateTime || event.start.date;
    if (!startDate) return '';
    
    const date = new Date(startDate);
    return date.toLocaleDateString('es-PR', { weekday: 'short', day: 'numeric' });
  };

  const groupEventsByDate = () => {
    const grouped = {};
    events.forEach(event => {
      const startDate = event.start?.dateTime || event.start?.date;
      if (!startDate) return;
      
      const dateKey = new Date(startDate).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    return grouped;
  };

  const monthName = currentDate.toLocaleDateString('es-PR', { month: 'long', year: 'numeric' });

  if (error && error.includes('no está conectado')) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => window.location.href = '/admin'}>
            Ir a Configuración
          </Button>
        </CardContent>
      </Card>
    );
  }

  const groupedEvents = groupEventsByDate();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-6 w-6 text-blue-500" />
          <h2 className="text-xl font-semibold">Google Calendar</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={handleSyncFromCalendar} 
            variant="outline" 
            size="sm" 
            disabled={syncing}
            title="Sincronizar cambios desde Calendar al CRM"
          >
            <ArrowDownUp className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            Sincronizar
          </Button>
          <Button 
            onClick={handleSyncAllToCalendar} 
            variant="outline" 
            size="sm" 
            disabled={syncing}
            title="Enviar todas las tareas pendientes al Calendar"
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Sync Tareas
          </Button>
          <Button onClick={loadEvents} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={() => {
            const today = new Date().toISOString().split('T')[0];
            setNewEvent(prev => ({ ...prev, start_date: today, end_date: today }));
            setShowCreateModal(true);
          }} className="bg-blue-500 hover:bg-blue-600">
            <Plus className="h-4 w-4 mr-1" />
            Nuevo Evento
          </Button>
        </div>
      </div>

      {/* Sync Message */}
      {syncMessage && (
        <div className={`p-3 rounded-lg text-sm ${
          syncMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {syncMessage.text}
        </div>
      )}

      {/* Info about sync */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <strong>Sincronización automática:</strong> Las tareas del CRM se sincronizan automáticamente con tu calendario. 
        Si eliminas un evento del calendario, la tarea se marcará como completada.
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h3 className="text-lg font-medium capitalize min-w-[200px] text-center">{monthName}</h3>
        <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {error && !error.includes('no está conectado') && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Events List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Cargando eventos...</p>
            </div>
          ) : Object.keys(groupedEvents).length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              No hay eventos este mes
            </div>
          ) : (
            <div className="divide-y">
              {Object.entries(groupedEvents)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([dateKey, dayEvents]) => (
                  <div key={dateKey} className="p-4">
                    <div className="text-sm font-medium text-gray-500 mb-3">
                      {new Date(dateKey).toLocaleDateString('es-PR', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long' 
                      })}
                    </div>
                    <div className="space-y-2">
                      {dayEvents.map(event => (
                        <div 
                          key={event.id}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                        >
                          <div className="w-1 h-full min-h-[40px] bg-blue-500 rounded-full" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Clock className="h-3 w-3 text-gray-400" />
                              <span className="text-xs text-gray-500">{formatEventTime(event)}</span>
                            </div>
                            <p className="font-medium text-gray-800">{event.summary}</p>
                            {event.location && (
                              <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                                <MapPin className="h-3 w-3" />
                                {event.location}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            {event.htmlLink && (
                              <a 
                                href={event.htmlLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                <ExternalLink className="h-4 w-4 text-gray-400" />
                              </a>
                            )}
                            <button 
                              onClick={() => handleDeleteEvent(event.id)}
                              className="p-1 hover:bg-red-100 rounded"
                            >
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Tasks to Sync */}
      {tareas.filter(t => t.status === 'pending' && t.due_date).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium text-gray-700 mb-3">Tareas pendientes para sincronizar</h4>
            <div className="space-y-2">
              {tareas
                .filter(t => t.status === 'pending' && t.due_date)
                .slice(0, 5)
                .map(tarea => (
                  <div key={tarea.id} className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                    <span className="text-sm">{tarea.title}</span>
                    <Button size="sm" variant="outline" onClick={() => syncTaskToCalendar(tarea)}>
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      Sincronizar
                    </Button>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-blue-500" />
              Nuevo Evento
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-y-auto py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Título *</label>
              <Input
                value={newEvent.summary}
                onChange={(e) => setNewEvent(prev => ({ ...prev, summary: e.target.value }))}
                placeholder="Título del evento"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Fecha Inicio *</label>
                <Input
                  type="date"
                  value={newEvent.start_date}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Hora Inicio</label>
                <Input
                  type="time"
                  value={newEvent.start_time}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Fecha Fin *</label>
                <Input
                  type="date"
                  value={newEvent.end_date}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Hora Fin</label>
                <Input
                  type="time"
                  value={newEvent.end_time}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Ubicación</label>
              <Input
                value={newEvent.location}
                onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Ubicación del evento"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Descripción</label>
              <textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción del evento..."
                className="w-full h-24 p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateEvent} 
              disabled={creating}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {creating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creando...</>
              ) : (
                <><Plus className="h-4 w-4 mr-2" /> Crear Evento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPanel;
